import { useMemo, useState } from 'react';
import {
    IconArrowsSort,
    IconCopy,
    IconFilePlus,
    IconRotate,
    IconRotateClockwise,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { PAGE_PRESETS } from '../constants';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import { MiniSelect } from '../ui/prims';
import { useThumbnails } from './useThumbnails';

/** Bulk page management: select, reorder, rotate, duplicate, delete, insert. */
export default function PagesModal({ onClose }) {
    const { t } = useTranslation();
    const pages = usePdfEditorStore((s) => s.pages);
    const pdf = usePdfEditorStore((s) => s.pdf);
    const rotatePage = usePdfEditorStore((s) => s.rotatePage);
    const deletePages = usePdfEditorStore((s) => s.deletePages);
    const duplicatePages = usePdfEditorStore((s) => s.duplicatePages);
    const insertBlankPage = usePdfEditorStore((s) => s.insertBlankPage);
    const movePage = usePdfEditorStore((s) => s.movePage);
    const setPages = usePdfEditorStore((s) => s.setPages);
    const goToPage = usePdfEditorStore((s) => s.goToPage);

    const [selected, setSelected] = useState(() => new Set());
    const [preset, setPreset] = useState('a4');
    const [dragIndex, setDragIndex] = useState(null);

    const sourceIndices = useMemo(
        () => pages.map((p) => p.sourceIndex).filter((i) => i != null),
        [pages],
    );
    const thumbs = useThumbnails(pdf, sourceIndices);

    const selectedUids = [...selected];
    const anySelected = selectedUids.length > 0;
    const targets = anySelected ? selectedUids : pages.map((p) => p.uid);

    const toggle = (uid) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(uid)) next.delete(uid);
        else next.add(uid);
        return next;
    });

    const rotateTargets = (delta) => targets.forEach((uid) => rotatePage(uid, delta));

    const removeTargets = () => {
        if (targets.length >= pages.length) return;
        deletePages(targets);
        setSelected(new Set());
    };

    const barBtn = 'flex h-9 items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-3 text-[12.5px] font-medium text-[#3D3833] transition-colors hover:border-primary/50 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200';

    return (
        <div
            className="fixed inset-0 z-[9100] flex items-center justify-center bg-[#1C1815]/45 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={t('pdfEditor.pages.title')}
        >
            <div className="flex h-full max-h-[86vh] w-full max-w-[1000px] flex-col overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_24px_64px_rgba(28,24,21,0.28)] dark:border-zinc-700 dark:bg-zinc-900">
                <div className="flex h-14 flex-none items-center justify-between border-b border-[#ECE8E4] px-4 dark:border-zinc-800">
                    <h2 className="text-[15px] font-semibold text-[#1C1815] dark:text-zinc-100">
                        {t('pdfEditor.pages.title')}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B635C] hover:bg-[#F4F1ED] dark:text-zinc-400 dark:hover:bg-zinc-800"
                    >
                        <IconX size={18} stroke={1.8} />
                    </button>
                </div>

                <div className="flex flex-none flex-wrap items-center gap-2 border-b border-[#ECE8E4] px-4 py-2.5 dark:border-zinc-800">
                    <span className="mr-1 text-[12px] font-medium text-[#6B635C] dark:text-zinc-400">
                        {anySelected
                            ? t('pdfEditor.pages.selectedCount', { count: toBnDigits(selectedUids.length) })
                            : t('pdfEditor.pages.allPages')}
                    </span>
                    <button type="button" className={barBtn} onClick={() => rotateTargets(-90)}>
                        <IconRotate size={15} stroke={1.8} />
                        {t('pdfEditor.pages.rotateLeft')}
                    </button>
                    <button type="button" className={barBtn} onClick={() => rotateTargets(90)}>
                        <IconRotateClockwise size={15} stroke={1.8} />
                        {t('pdfEditor.pages.rotateRight')}
                    </button>
                    <button type="button" className={barBtn} onClick={() => duplicatePages(targets)}>
                        <IconCopy size={15} stroke={1.8} />
                        {t('pdfEditor.pages.duplicate')}
                    </button>
                    <button
                        type="button"
                        className={`${barBtn} !text-[#D33A2C]`}
                        onClick={removeTargets}
                        disabled={targets.length >= pages.length}
                    >
                        <IconTrash size={15} stroke={1.8} />
                        {t('pdfEditor.pages.delete')}
                    </button>
                    <button
                        type="button"
                        className={barBtn}
                        onClick={() => setPages([...pages].reverse())}
                    >
                        <IconArrowsSort size={15} stroke={1.8} />
                        {t('pdfEditor.pages.reverse')}
                    </button>

                    <div className="ml-auto flex items-center gap-2">
                        <div className="w-[110px]">
                            <MiniSelect
                                value={preset}
                                onChange={setPreset}
                                options={PAGE_PRESETS.map((p) => ({ value: p.id, label: p.label }))}
                            />
                        </div>
                        <button
                            type="button"
                            className={barBtn}
                            onClick={() => {
                                const size = PAGE_PRESETS.find((p) => p.id === preset);
                                const at = pages.findIndex((p) => p.uid === selectedUids[selectedUids.length - 1]);
                                insertBlankPage(at >= 0 ? at : pages.length - 1, size);
                            }}
                        >
                            <IconFilePlus size={15} stroke={1.8} />
                            {t('pdfEditor.pages.insertBlank')}
                        </button>
                    </div>
                </div>

                <div className="grid min-h-0 flex-1 grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-4 overflow-y-auto p-4 [scrollbar-width:thin]">
                    {pages.map((page, index) => {
                        const src = page.sourceIndex != null ? thumbs[page.sourceIndex] : null;
                        const isSelected = selected.has(page.uid);
                        const rotatedThumb = page.userRotation % 180 === 90;
                        return (
                            <div
                                key={page.uid}
                                draggable
                                onDragStart={() => setDragIndex(index)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (dragIndex != null && dragIndex !== index) movePage(dragIndex, index);
                                    setDragIndex(null);
                                }}
                                className="flex flex-col items-center"
                            >
                                <button
                                    type="button"
                                    onClick={() => toggle(page.uid)}
                                    onDoubleClick={() => { goToPage(index); onClose(); }}
                                    className={`w-full overflow-hidden rounded-lg border-2 bg-white transition-colors ${
                                        isSelected ? 'border-primary ring-2 ring-primary/25' : 'border-[#ECE8E4] hover:border-primary/50 dark:border-zinc-700'
                                    }`}
                                    style={{ aspectRatio: rotatedThumb ? `${page.height} / ${page.width}` : `${page.width} / ${page.height}` }}
                                >
                                    {src ? (
                                        <img
                                            src={src}
                                            alt=""
                                            draggable={false}
                                            className="h-full w-full object-contain"
                                            style={{ transform: `rotate(${page.userRotation}deg)` }}
                                        />
                                    ) : (
                                        <div className="h-full w-full bg-white dark:bg-zinc-100" />
                                    )}
                                </button>
                                <span className="mt-1.5 text-[11.5px] font-medium text-[#6B635C] dark:text-zinc-400">
                                    {toBnDigits(index + 1)}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div className="flex h-14 flex-none items-center justify-end gap-2 border-t border-[#ECE8E4] px-4 dark:border-zinc-800">
                    <button
                        type="button"
                        onClick={() => setSelected(new Set())}
                        className={barBtn}
                        disabled={!anySelected}
                    >
                        {t('pdfEditor.pages.clearSelection')}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-9 items-center rounded-lg bg-primary px-4 text-[13px] font-semibold text-white hover:bg-primary-700"
                    >
                        {t('common.done')}
                    </button>
                </div>
            </div>
        </div>
    );
}
